import { useCallback, useEffect, useMemo, useState } from 'react';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { WaitingSettlementsSummary } from '@/types/accounts';

/** AC8: nothing waiting — the resting state the banner renders as nothing new. */
const EMPTY: WaitingSettlementsSummary = {
  waiting: [], mappedNotRetried: [], booking: [], removed: [],
  entryFailedCount: 0, cashMethodNeedsReviewCount: 0, transactionLabels: {},
};

/**
 * FIN-1021: AC9, derived from server data on every load — no client-side memory of a prior
 * waiting count survives a reload. EC7: a load failure is a distinct, visible state (`loadFailed`),
 * never silently treated as "nothing waiting" (summary stays the last-known value, or EMPTY on
 * the very first load).
 */
export function useWaitingSettlements() {
  const [summary, setSummary] = useState<WaitingSettlementsSummary>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const accountService = useMemo(() => ServiceRegistry.getAccountService(), []);

  // `reload` is the manual retrigger the "Retry booking" button uses -- a click handler, never
  // called from inside an effect, so it may call setState however it needs to (including
  // setLoading(true) up front).
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const result = await accountService.getWaitingSettlements();
      setSummary(result);
      setLoadFailed(false);
    } catch (error) {
      console.error('Failed to load waiting settlements:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [accountService]);

  // The mount fetch is deliberately its OWN inline async function, not a call out to `reload` or
  // any other named helper -- react-hooks' set-state-in-effect rule traces into a function an
  // effect calls (by reference, including through useCallback) to see whether it reaches a
  // setState call, so routing through a shared helper still trips it. This is React's own
  // documented "Fetching data" effect shape (a `cancelled` guard, an async function declared and
  // invoked directly in the effect body) -- `loading` already starts `true` for this first call.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await accountService.getWaitingSettlements();
        if (cancelled) return;
        setSummary(result);
        setLoadFailed(false);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load waiting settlements:', error);
        setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountService]);

  return { summary, loading, loadFailed, reload };
}
